import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Wallet } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AdminAdjustWalletDialog,
  type AdminAdjustWalletTarget,
} from "@/components/admin/AdminAdjustWalletDialog";
import { DataTable, PageHeader, type DataTableColumn } from "@/components/shared";
import { useAdminUserWallet, useAdminWalletTransactions } from "@/hooks/queries";
import { TransactionType } from "@/types/enums";
import type { WalletTransactionResponseDto } from "@/types/wallet.dto";
import { cn } from "@/lib/utils";

const AdminUserWallet = () => {
  const { userId = "" } = useParams();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [adjustOpen, setAdjustOpen] = useState(false);

  const { data: wallet, isLoading: walletLoading } = useAdminUserWallet(userId, Boolean(userId));
  const { data: txData, isLoading: txLoading } = useAdminWalletTransactions(
    { userId, page, pageSize },
    Boolean(userId),
  );

  const profile = useMemo(() => {
    const tx = txData?.items?.[0];
    return {
      name: tx?.userName || "User",
      email: tx?.userEmail || "",
      memberCode: tx?.memberCode,
    };
  }, [txData]);

  const adjustTarget: AdminAdjustWalletTarget | null = userId
    ? {
        userId,
        name: profile.name,
        email: profile.email,
        memberCode: profile.memberCode,
      }
    : null;

  const columns: DataTableColumn<WalletTransactionResponseDto>[] = [
    {
      id: "txn",
      header: "Transaction",
      cell: (row) => (
        <div>
          <p className="font-mono text-xs font-medium">{row.transactionId}</p>
          <p className="text-xs text-muted-foreground">{row.categoryText}</p>
        </div>
      ),
    },
    {
      id: "type",
      header: "Type",
      cell: (row) => (
        <Badge variant={row.type === TransactionType.Credit ? "default" : "secondary"}>
          {row.typeText}
        </Badge>
      ),
    },
    {
      id: "amount",
      header: "Amount",
      align: "right",
      cell: (row) => (
        <span
          className={cn(
            "font-semibold",
            row.type === TransactionType.Credit ? "text-success" : "text-destructive",
          )}
        >
          {row.type === TransactionType.Credit ? "+" : "-"}₹{row.amount.toFixed(2)}
        </span>
      ),
    },
    {
      id: "balance",
      header: "Balance after",
      align: "right",
      cell: (row) => `₹${row.balanceAfter.toFixed(2)}`,
    },
    {
      id: "desc",
      header: "Description",
      cell: (row) => <p className="max-w-[280px] truncate text-xs text-muted-foreground">{row.description}</p>,
    },
    {
      id: "date",
      header: "Date",
      cell: (row) =>
        new Date(row.createdAt).toLocaleString("en-IN", {
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
    },
  ];

  return (
    <div className="space-y-6">
      <Button variant="outline" size="sm" asChild>
        <Link to="/admin/wallet/transactions">
          <ArrowLeft className="mr-1 h-4 w-4" /> Back to transactions
        </Link>
      </Button>

      <PageHeader
        title="User Wallet"
        description={`${profile.name}${profile.email ? ` · ${profile.email}` : ""}${profile.memberCode ? ` · ${profile.memberCode}` : ""}`}
      />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base">Balance</CardTitle>
          <Button size="sm" variant="outline" className="gap-1" onClick={() => setAdjustOpen(true)}>
            <Wallet className="h-3.5 w-3.5" /> Adjust
          </Button>
        </CardHeader>
        <CardContent>
          {walletLoading ? (
            <Skeleton className="h-10 w-40" />
          ) : (
            <div className="flex flex-wrap gap-6">
              <div>
                <p className="text-xs text-muted-foreground">Available</p>
                <p className="text-2xl font-heading font-bold">
                  ₹{(wallet?.availableBalance ?? 0).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total credits</p>
                <p className="text-lg font-semibold text-success">
                  ₹{(wallet?.totalCredits ?? 0).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total debits</p>
                <p className="text-lg font-semibold text-destructive">
                  ₹{(wallet?.totalDebits ?? 0).toFixed(2)}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {txLoading ? (
        <Skeleton className="h-96 rounded-xl" />
      ) : (
        <DataTable
          title="Wallet ledger"
          columns={columns}
          data={txData?.items ?? []}
          getRowId={(row) => String(row.id)}
          emptyMessage="No transactions for this user yet."
          pagination={{
            page,
            pageSize: txData?.pageSize ?? pageSize,
            totalCount: txData?.totalItems ?? 0,
            totalPages: txData?.totalPages ?? 1,
            from: txData?.totalItems ? (page - 1) * (txData?.pageSize ?? pageSize) + 1 : 0,
            to: Math.min(page * (txData?.pageSize ?? pageSize), txData?.totalItems ?? 0),
          }}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      )}

      <AdminAdjustWalletDialog
        open={adjustOpen}
        onOpenChange={setAdjustOpen}
        target={adjustTarget}
      />
    </div>
  );
};

export default AdminUserWallet;
